export const VIEWPORT_RULER_THICKNESS = 18;
export const VIEWPORT_OVERLAY_STROKE_PAD = 1;

type CanvasDisplayStyleProperty = 'width' | 'height' | 'marginLeft' | 'marginTop';

interface ViewportSize {
  w: number;
  h: number;
}

export interface ViewportCanvasMetrics {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface ViewportCanvasFrame {
  wrapW: number;
  wrapH: number;
  canvasX: number;
  canvasY: number;
}

let viewportCanvasFrame: ViewportCanvasFrame | null = null;
let viewportCanvasFrameObserver: ResizeObserver | null = null;
let viewportRulersVisible = true;

export function setViewportRulersVisible(visible: boolean): void {
  if (viewportRulersVisible === visible) return;
  viewportRulersVisible = visible;
  const wrap = document.getElementById('viewport-canvas-wrap');
  if (wrap) {
    updateViewportCanvasFrame(wrap.clientWidth, wrap.clientHeight);
  }
}

export function observeViewportCanvasFrame(onResize: () => void): void {
  const wrap = document.getElementById('viewport-canvas-wrap');
  if (!wrap) return;

  viewportCanvasFrameObserver?.disconnect();
  updateViewportCanvasFrame(wrap.clientWidth, wrap.clientHeight);
  if (!('ResizeObserver' in window)) return;

  let viewportResizeFrameRequested = false;
  const observer = new ResizeObserver(entries => {
    const entry = entries.find(item => item.target === wrap);
    if (!entry) return;

    const size = getResizeObserverEntrySize(entry);
    if (!updateViewportCanvasFrame(size.w, size.h)) return;
    if (viewportResizeFrameRequested) return;

    viewportResizeFrameRequested = true;
    requestAnimationFrame(() => {
      viewportResizeFrameRequested = false;
      onResize();
    });
  });
  observer.observe(wrap);
  viewportCanvasFrameObserver = observer;
}

export function getViewportCanvasFrameSize(): ViewportSize | null {
  const frame = getViewportCanvasFrame();
  if (!frame) return null;
  return {
    w: Math.max(1, frame.wrapW - VIEWPORT_OVERLAY_STROKE_PAD * 2),
    h: Math.max(1, frame.wrapH - VIEWPORT_OVERLAY_STROKE_PAD * 2),
  };
}

export function syncViewportCanvasDisplay(size: ViewportSize): ViewportCanvasMetrics {
  const canvas = document.getElementById('viewport-canvas') as HTMLCanvasElement | null;
  if (!canvas) return { x: 0, y: 0, w: size.w, h: size.h };

  const frame = getViewportCanvasFrame();
  if (!frame) return { x: 0, y: 0, w: size.w, h: size.h };

  const availableW = Math.max(1, frame.wrapW - VIEWPORT_OVERLAY_STROKE_PAD * 2);
  const availableH = Math.max(1, frame.wrapH - VIEWPORT_OVERLAY_STROKE_PAD * 2);
  const scale = Math.min(
    1,
    availableW / size.w,
    availableH / size.h,
  );
  const displayW = Math.max(1, Math.floor(size.w * scale));
  const displayH = Math.max(1, Math.floor(size.h * scale));

  setStylePx(canvas, 'marginLeft', VIEWPORT_OVERLAY_STROKE_PAD);
  setStylePx(canvas, 'marginTop', VIEWPORT_OVERLAY_STROKE_PAD);
  setStylePx(canvas, 'width', displayW);
  setStylePx(canvas, 'height', displayH);

  return {
    x: frame.canvasX + VIEWPORT_OVERLAY_STROKE_PAD,
    y: frame.canvasY + VIEWPORT_OVERLAY_STROKE_PAD,
    w: displayW,
    h: displayH,
  };
}

function getViewportCanvasFrame(): ViewportCanvasFrame | null {
  const wrap = document.getElementById('viewport-canvas-wrap');
  if (!wrap) return viewportCanvasFrame;

  if (!viewportCanvasFrame || viewportCanvasFrame.wrapW <= 0 || viewportCanvasFrame.wrapH <= 0) {
    updateViewportCanvasFrame(wrap.clientWidth, wrap.clientHeight);
  }
  return viewportCanvasFrame;
}

function updateViewportCanvasFrame(wrapW: number, wrapH: number): boolean {
  const next: ViewportCanvasFrame = {
    wrapW: Math.max(0, wrapW),
    wrapH: Math.max(0, wrapH),
    canvasX: viewportRulersVisible ? VIEWPORT_RULER_THICKNESS : 0,
    canvasY: viewportRulersVisible ? VIEWPORT_RULER_THICKNESS : 0,
  };

  const prev = viewportCanvasFrame;
  if (
    prev
    && prev.wrapW === next.wrapW
    && prev.wrapH === next.wrapH
    && prev.canvasX === next.canvasX
    && prev.canvasY === next.canvasY
  ) {
    return false;
  }

  viewportCanvasFrame = next;
  return true;
}

function getResizeObserverEntrySize(entry: ResizeObserverEntry): ViewportSize {
  const boxSize = Array.isArray(entry.contentBoxSize) ? entry.contentBoxSize[0] : entry.contentBoxSize;
  if (boxSize) {
    return { w: boxSize.inlineSize, h: boxSize.blockSize };
  }

  return { w: entry.contentRect.width, h: entry.contentRect.height };
}

function setStylePx(element: HTMLElement, property: CanvasDisplayStyleProperty, value: number): void {
  const next = `${Math.round(value)}px`;
  if (element.style[property] === next) return;
  element.style[property] = next;
}
